"use client";
import { cn } from "@/lib/utils";
import { useSidebarStore } from "@/zustand-hooks/sidebar";
import { ListCollapse, User } from "lucide-react";
import React, { useState } from "react";
import FullScreenToggle from "./FullScreenToggle";
import Notifications from "./Notifications";
import ChangeLanguage from "./ChangeLanguage";
import { useUser } from "@/zustand-hooks/user-hook";
import { Link } from "@/i18n/routing";
import Avatar from "./Avatar";
import { ThemeToggle } from "./ThemeToggle";
import Balance from "./Balance";
import "../../components/scrollbars/semi-transparent.css";
import { useLocale, useTranslations } from "next-intl";
import { NotificationType } from "@/react-queries/queries/notifications/types";
import { Modal } from "./ModalComponent";
import { notification_templates, replacer } from "@/lib/notifications";
import Responsive from "./Responsive";
import { Button } from "../ui/button";
import { Sheet } from "../ui/sheet";
import HeaderUserSidebar from "./HeaderUserSidebar";
import useIsMounted from "@/hooks/useIsMounted";

const Header = () => {
    const isMounted = useIsMounted()

    const locale = useLocale();
    const translate = useTranslations("Header");
    const translate_buttons = useTranslations("buttons");
    const [notification, setNotification] = useState<
        NotificationType | undefined
    >();
    const { isCollapsed, toggleCollapsed, toggleUserSidebarCollapsed, isUserCollapsed } = useSidebarStore();
    const { user } = useUser();


    if (!isMounted) return null

    return (
        <>
            <div className="h-[70px] py-4 flex items-center px-4 lg:px-8 bg-gradient-to-r from-chrome-900 via-chrome-800 to-chrome-900 dark:from-chrome-950 dark:via-chrome-900 dark:to-chrome-950 border-b border-chrome-700/50 shadow-lg">
                <Responsive query="(min-width:900px)">
                    <div className="h-full w-full flex justify-between items-center">
                        <ListCollapse
                            onClick={() => {
                                toggleCollapsed();
                            }}
                            className={cn(
                                "cursor-pointer box-content text-chrome-400 hover:text-chrome-100 hover:bg-chrome-100/10 p-2 rounded-lg transition-all duration-200",
                                !!isCollapsed
                                    ? "scale-100 hover:scale-110 active:scale-95"
                                    : "-scale-100 hover:-scale-110 active:-scale-95"
                            )}
                        />
                        <div className="flex items-center gap-5 ml-auto">
                            <div className="flex items-center gap-4">
                                <Balance />
                                <Notifications setNotification={setNotification} />
                                <div className="hidden xl:flex items-center gap-3">
                                    <div className="h-6 w-px bg-chrome-600"></div>
                                    <ThemeToggle />
                                    <FullScreenToggle />
                                    <ChangeLanguage />
                                </div>
                            </div>
                            <div className="text-chrome-400">
                                {user?.id ? (
                                    <p className="text-sm font-medium">{user.email}</p>
                                ) : (
                                    <Link href="/login" className="text-brand-400 hover:text-brand-500 transition-colors">
                                        {translate_buttons("sign_in")}
                                    </Link>
                                )}
                            </div>
                            <Avatar />
                        </div>
                    </div>

                </Responsive>
                <Responsive query="(max-width:900px)">
                    <div className="flex items-center justify-end w-full gap-2">
                        <span className="text-sm text-chrome-400">
                            {
                                user?.email ? user.email.slice(0, 10).concat('...') : 'Unknown'
                            }
                        </span>
                        <Button
                            onClick={toggleUserSidebarCollapsed}
                            className="w-[48px] h-[48px] p-0 flex items-center justify-center bg-chrome-700 text-chrome-400 rounded-xl hover:bg-chrome-600 active:scale-90 transition-all">
                            <User />
                        </Button>
                    </div>
                    <HeaderUserSidebar setNotification={setNotification} />
                </Responsive>
            </div>
            {notification && (
                <Modal
                    isOpen={!!notification}
                    onClose={() => {
                        setNotification(undefined);
                    }}
                    title={translate("notifications.title")}
                    description=""
                    isLoading={false}
                >
                    <div className="flex flex-col gap-4">
                        <p className="text-lg font-bold">{notification.title}</p>
                        <p className="text-md">
                            {replacer(
                                // @ts-ignore
                                notification_templates[locale][notification.type],
                                notification.items
                            )}
                        </p>
                        <hr />
                        <div className="flex items-center justify-between">
                            <p className="font-semibold flex gap-1 items-center">
                                {translate("notifications.status")}:{" "}
                                <span className="px-2 py-1 text-xs rounded bg-emerald-500 text-white">
                                    {translate("notifications.read")}
                                </span>
                            </p>
                            <p className="flex items-center gap-1 text-sm">
                                <span className="font-semibold">
                                    {new Date(notification.date).toLocaleDateString()}
                                </span>
                                <span className="text-gray-500">
                                    {new Date(notification.date).toLocaleTimeString()}
                                </span>
                            </p>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default Header;
